import * as React from 'react';
import { Input, type InputProps } from './Input';

/**
 * Formats as a US number while it is being typed, and otherwise leaves the
 * value alone.
 *
 * Ported from the app's original PhoneInput. Two deliberate differences:
 *
 *   • The original bailed out as soon as the value contained a bracket. Since
 *     its own formatting inserts one at the fourth digit, typing a number
 *     straight through produced "(123) 4567890" — it only ever reached
 *     "(123) 456-7890" if the whole value arrived at once, which is what its
 *     test did. Reformatting from the digits fixes typing and still yields the
 *     same result for a pasted, already-formatted number.
 *
 *   • The original stripped a leading `+`, silently turning a valid
 *     international number into a wrong US one. Anything containing `+` or `.`
 *     is now passed through untouched.
 */
export function formatPhoneNumber(value: string): string {
  const cleaned = value.replace(/[^\d+() .-]/g, '');

  // A `+` or `.` means the user is writing an international or dotted format;
  // imposing US brackets on that would fight them.
  if (cleaned.includes('+') || cleaned.includes('.')) return cleaned;

  const digits = cleaned.replace(/\D/g, '');
  if (digits.length === 10) {
    return digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }
  if (digits.length > 6) {
    return digits.replace(/(\d{3})(\d{3})(\d{0,4})/, '($1) $2-$3');
  }
  if (digits.length > 3) {
    return digits.replace(/(\d{3})(\d{0,3})/, '($1) $2');
  }
  return cleaned;
}

/**
 * Formats as a US number with its country code, +1 (555)-123-4567, while it
 * is being typed.
 *
 * Built from the digits every time, so typing, deleting and pasting all land
 * on the same shape. A leading 1 is the country code, not part of the number:
 * no US area code starts with 1. Anything past ten digits is dropped.
 * Separators only appear once a digit follows them, so backspacing over one
 * removes it instead of the shape putting it straight back.
 *
 * A number that starts with + and another country code, like +44, is left as
 * typed. Forcing it into the US shape would turn it into a wrong US number.
 */
export function formatPhoneWithCountryCode(value: string): string {
  const cleaned = value.replace(/[^\d+() .-]/g, '');
  const typedPlus = cleaned.trimStart().startsWith('+');
  // A lone + stays too, until the next digit says which country it is.
  if (typedPlus && cleaned.replace(/\D/g, '')[0] !== '1') return cleaned;

  let digits = cleaned.replace(/\D/g, '');
  if (digits.startsWith('1')) digits = digits.slice(1);
  digits = digits.slice(0, 10);

  // "+1" on its own is someone typing the code, or backspacing to it. Keep it
  // so the next backspace can take the 1, and the one after the +.
  if (digits.length === 0) return typedPlus ? '+1' : '';
  const area = digits.slice(0, 3);
  const prefix = digits.slice(3, 6);
  const line = digits.slice(6);
  if (digits.length <= 3) return `+1 (${area}`;
  if (digits.length <= 6) return `+1 (${area})-${prefix}`;
  return `+1 (${area})-${prefix}-${line}`;
}

/** True when the value could plausibly be a phone number. */
export function isValidPhoneNumber(value: string): boolean {
  if (value.length < 5) return false;
  return /^[+]?[\d() .-]{5,20}$/.test(value);
}

/**
 * The rule for a field formatted by `formatPhoneWithCountryCode`.
 *
 * The +1 ( prefix alone is five characters, so the loose check above passes
 * a number after one digit. A US number here needs all ten digits after the
 * country code. A number with another country code keeps the loose check.
 * Treat an empty field, or one left at "+1", as no phone before calling this.
 */
export function isValidPhoneWithCountryCode(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.startsWith('+') && trimmed.replace(/\D/g, '')[0] !== '1') {
    return isValidPhoneNumber(trimmed);
  }
  let digits = trimmed.replace(/\D/g, '');
  if (digits.startsWith('1')) digits = digits.slice(1);
  return digits.length === 10;
}

export const PHONE_WITH_COUNTRY_CODE_MESSAGE =
  'Enter all 10 digits, or start with + and the country code for a number outside the US.';

export const PHONE_VALIDATION_MESSAGE =
  'Enter a valid phone number — at least 5 characters, using only digits, +, -, ., ( ) and spaces.';

export interface PhoneInputProps extends Omit<InputProps, 'type' | 'onChange'> {
  value?: string;
  /** Receives the formatted value, not the raw keystrokes. */
  onChange?: (value: string) => void;
  /**
   * Writes the number as +1 (555)-123-4567 instead of (555) 123-4567. A
   * number typed with another country code, like +44, is left as typed.
   */
  withCountryCode?: boolean;
}

/**
 * A phone number field.
 *
 * Formats as you type and reports the formatted value, so what is stored
 * matches what is shown. Put it in a `Field` for the label and error, exactly
 * like `Input`:
 *
 *   <Field label="Contact phone" error={errors.phone?.message}>
 *     <PhoneInput value={phone} onChange={setPhone} />
 *   </Field>
 *
 * `withCountryCode` writes it as +1 (555)-123-4567, the shape the add-member
 * form asks for.
 *
 * Unlike the original this takes no `register` prop and renders no label of its
 * own — it is a control, and `Field` owns the labelling. With react-hook-form,
 * wrap it in a `Controller`; the exported `formatPhoneNumber` and
 * `isValidPhoneNumber` are there for building the rules.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  function PhoneInput(
    { value, onChange, withCountryCode = false, placeholder, ...props },
    ref
  ) {
    const format = withCountryCode
      ? formatPhoneWithCountryCode
      : formatPhoneNumber;
    return (
      <Input
        ref={ref}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        maxLength={20}
        placeholder={
          placeholder ??
          (withCountryCode ? '+1 (555)-123-4567' : '(555) 123-4567')
        }
        value={value}
        onChange={(event) => onChange?.(format(event.target.value))}
        {...props}
      />
    );
  }
);
